
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Organization } from "@/types/organization";
import { resolveSubdomain } from "@/lib/subdomain-utils";

export const useOrganization = () => {
  // Use centralized subdomain resolution
  const subdomain = useMemo(() => resolveSubdomain(), []);

  // Memoize query options to prevent unnecessary re-renders
  const queryOptions = useMemo(() => ({
    queryKey: ['organization', subdomain],
    queryFn: async (): Promise<Organization> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('subdomain', subdomain)
        .eq('is_active', true)
        .single();

      if (error) {
        // If organization not found, fallback to Swift
        if (error.code === 'PGRST116') {
          const { data: swiftOrg, error: swiftError } = await supabase
            .from('organizations')
            .select('*')
            .eq('subdomain', 'swift')
            .single();
          
          if (swiftError) throw swiftError;
          return swiftOrg;
        }
        throw error;
      }

      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  }), [subdomain]);

  const { data: organization, isLoading, error } = useQuery(queryOptions);

  return {
    organization,
    isLoading,
    error,
    subdomain,
  };
};
